import { Calculator } from '@kmx/online-finance-helpers';
import {
  QueryClientContext,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';
import {
  getCalculatorDefaults,
  getCreditRates,
  getTaxesAndFees,
  getVisitorCalculatorPreferences,
  putVisitorCalculatorPreferences,
} from '../api/api';
import { CalculatorConfigContext } from '../context/CalculatorContextProvider';
import type {
  CreditRating,
  ICalculatorDefaults,
  ICalculatorVisitorPreferences,
  IFinanceRateInfo,
  IInlinePreferences,
  IInlineRequestPreferences,
  ITaxesAndFees,
  PersonalFinanceEnvironment,
} from '../types';
import { currencyFormat } from '../utils/numbers';

export interface ICalculateMonthlyPaymentResponse {
  calculatedMonthlyPayment: number;
  calculatedTotalFinanceAmount: number;
  disclaimer: string;
  calculationDetails: {
    taxesAndFees: number;
    apr: number;
    downPayment: number;
    creditRating: CreditRating;
    termLength: number;
    taxState: string;
  };
}

export interface ICalculateVehiclePriceResponse {
  calculatedVehiclePrice: number;
  calculatedTotalFinanceAmount: number;
  disclaimer: string;
  calculationDetails: {
    apr: number;
    creditRating: CreditRating;
    downPayment: number;
    taxesAndFees: number;
    taxState: string;
    termLength: number;
  };
}

const defaultCalculatorData = {
  preferences: {
    visitorId: 'defaults',
    termLength: 72,
    downPayment: 2500,
    monthlyPayment: 400,
    creditRating: 'Excellent' as const,
    prefersLongestTermLength: true,
    hasInteraction: false,
  },
  termLengths: [36, 48, 60, 72],
  rates: [
    {
      type: 'Excellent' as const,
      minimumRate: 5.24,
      maximumRate: 8.49,
      medianRate: 5.49,
      minimumFicoScore: 800,
      maximumFicoScore: null,
    },
    {
      type: 'VeryGood' as const,
      minimumRate: 7.44,
      maximumRate: 11.24,
      medianRate: 8.24,
      minimumFicoScore: 740,
      maximumFicoScore: 799,
    },
    {
      type: 'Good' as const,
      minimumRate: 11.29,
      maximumRate: 15.04,
      medianRate: 12.04,
      minimumFicoScore: 670,
      maximumFicoScore: 739,
    },
    {
      type: 'Fair' as const,
      minimumRate: 15.74,
      maximumRate: 19.89,
      medianRate: 16.89,
      minimumFicoScore: 580,
      maximumFicoScore: 669,
    },
    {
      type: 'Challenged' as const,
      minimumRate: 20.09,
      maximumRate: 24.59,
      medianRate: 21.59,
      minimumFicoScore: null,
      maximumFicoScore: 579,
    },
  ],
};

function isValidStoreId(storeId: string): boolean {
  try {
    return storeId != null && storeId !== '' && Number(storeId) > 0;
  } catch {
    return false;
  }
}

function writeDisclaimer(
  totalFinanceAmount: number,
  taxesAndFees: number,
  stateAbbreviation: string,
): string {
  return `Estimate is based on the information you provide and a total finance amount of ${currencyFormat(totalFinanceAmount)}, which includes ${currencyFormat(taxesAndFees)} in ${stateAbbreviation} estimated tax, title, and fees. Estimated values are for illustration purposes only, and don't constitute an advertisement or offer of specific credit terms. APRs and terms used in estimates may be unavailable based on vehicle, state of purchase, or your credit profile. Actual credit terms are subject to approval and availability.`;
}

function visitorPreferencesQueryOptions(
  environment: PersonalFinanceEnvironment,
  visitorId: string,
) {
  return queryOptions({
    enabled: visitorId.length > 0,
    queryKey: [
      'calculator_visitorCalculatorPreferences',
      environment,
      visitorId,
    ],
    queryFn: () =>
      visitorId
        ? getVisitorCalculatorPreferences(environment, visitorId).catch(() => {
            console.error(
              '@kmx/personal-finance-component failed API Call, falling back to default values.',
            );
            return defaultCalculatorData.preferences;
          })
        : Promise.resolve(defaultCalculatorData.preferences),
    refetchOnWindowFocus: false,
    retry: 5,
  });
}

function calculatorDefaultsQueryOptions(
  environment: PersonalFinanceEnvironment,
  visitorId: string,
) {
  return queryOptions({
    enabled: visitorId.length > 0,
    queryKey: ['calculator_defaults', environment, visitorId],
    queryFn: () =>
      visitorId
        ? getCalculatorDefaults(environment, visitorId).catch(() => {
            console.error(
              '@kmx/personal-finance-component failed API Call, falling back to default values.',
            );
            return withAprByCreditRating(defaultCalculatorData);
          })
        : Promise.resolve(withAprByCreditRating(defaultCalculatorData)),
    refetchOnWindowFocus: false,
    retry: 5,
  });
}

function feeScheduleQueryOptions(
  environment: PersonalFinanceEnvironment,
  storeId: string,
) {
  return queryOptions({
    enabled: isValidStoreId(storeId),
    queryKey: ['calculator_getFeeSchedule', environment, storeId],
    queryFn: () => getTaxesAndFees(environment, Number(storeId)),
    refetchOnWindowFocus: false,
    retry: 5,
  });
}

function ratesQueryOptions(environment: PersonalFinanceEnvironment) {
  return queryOptions({
    queryKey: ['calculator_getRates', environment],
    queryFn: () => getCreditRates(environment),
    refetchOnWindowFocus: false,
    retry: 5,
  });
}

function useCalculatorConfig() {
  const config = useContext(CalculatorConfigContext);
  const queryClient = useContext(QueryClientContext);
  if (!queryClient || !config) {
    const message =
      '@kmx/personal-finance-component error: useCalculator requires a CalculatorContextProvider in the React tree. Add <CalculatorContextProvider> to the root of your application.';
    console.error(message);
    throw new Error(message);
  }
  return { ...config, queryClient };
}

function withAprByCreditRating(
  defaults:
    | Omit<ICalculatorDefaults, 'aprByCreditRating'>
    | ICalculatorDefaults,
): ICalculatorDefaults {
  const aprByCreditRating = defaults.rates.reduce(
    (rates, rate) => {
      rates[rate.type] = rate.medianRate;
      return rates;
    },
    {} as Record<CreditRating, number>,
  );
  return { ...defaults, aprByCreditRating };
}

const fallbackDefaults = withAprByCreditRating(defaultCalculatorData);

function resolvePreferencesAndApr(
  preferences: ICalculatorVisitorPreferences,
  rates: IFinanceRateInfo[],
  overridePreferences?: IInlinePreferences,
) {
  const resolvedPreferences = overridePreferences
    ? { ...preferences, ...overridePreferences }
    : preferences;
  const mappedRates = rates.reduce(
    (result, rate) => {
      result[rate.type] = rate;
      return result;
    },
    {} as Record<CreditRating, IFinanceRateInfo>,
  );
  const rate = mappedRates[resolvedPreferences.creditRating];
  if (!rate) {
    throw new Error(
      `cannot resolve preferred credit rating '${resolvedPreferences.creditRating}' to an APR`,
    );
  }
  return { preferences: resolvedPreferences, apr: rate.medianRate };
}

function buildMonthlyPaymentResponse(
  preferences: ICalculatorVisitorPreferences,
  taxesAndFees: ITaxesAndFees,
  rates: IFinanceRateInfo[],
  vehiclePrice: number,
  overridePreferences?: IInlinePreferences,
  tradeInEquity = 0,
  maxCareCost = 0,
): ICalculateMonthlyPaymentResponse | null {
  try {
    const resolved = resolvePreferencesAndApr(
      preferences,
      rates,
      overridePreferences,
    );
    const fees = Calculator.lookupTaxesAndFeesByVehiclePrice(
      taxesAndFees.taxesAndFees,
      vehiclePrice,
    );
    const totalFinanceAmount = Calculator.calculateTotalFinancedAmount(
      vehiclePrice,
      resolved.preferences.downPayment,
      fees,
      tradeInEquity,
      maxCareCost,
    );
    const monthlyPayment = Calculator.calculateMonthlyPayment(
      vehiclePrice,
      resolved.preferences.downPayment,
      fees,
      resolved.preferences.termLength,
      resolved.apr,
      tradeInEquity,
      maxCareCost,
    );
    return {
      calculatedMonthlyPayment: Math.max(0, Math.round(monthlyPayment)),
      calculatedTotalFinanceAmount: Math.max(0, Math.round(totalFinanceAmount)),
      disclaimer: writeDisclaimer(
        Math.max(0, Math.round(totalFinanceAmount)),
        fees,
        taxesAndFees.stateAbbreviation,
      ),
      calculationDetails: {
        taxesAndFees: fees,
        apr: resolved.apr,
        downPayment: resolved.preferences.downPayment,
        creditRating: resolved.preferences.creditRating,
        termLength: resolved.preferences.termLength,
        taxState: taxesAndFees.stateAbbreviation,
      },
    };
  } catch (error) {
    console.info(
      '@kmx/personal-finance-component calculateMonthlyPayment error',
      error,
    );
    return null;
  }
}

function buildVehiclePriceResponse(
  preferences: ICalculatorVisitorPreferences,
  taxesAndFees: ITaxesAndFees,
  rates: IFinanceRateInfo[],
  overridePreferences?: IInlinePreferences,
  tradeInEquity = 0,
  maxCareCost = 0,
): ICalculateVehiclePriceResponse | null {
  try {
    const resolved = resolvePreferencesAndApr(
      preferences,
      rates,
      overridePreferences,
    );
    const calculation = Calculator.calculateVehicleBudget(
      resolved.preferences.monthlyPayment,
      resolved.preferences.downPayment,
      resolved.preferences.termLength,
      resolved.apr,
      taxesAndFees.taxesAndFees,
      tradeInEquity,
      maxCareCost,
    );
    return {
      calculatedVehiclePrice: Math.round(calculation.vehiclePrice),
      calculatedTotalFinanceAmount: Math.round(calculation.totalFinancedAmount),
      disclaimer: writeDisclaimer(
        Math.max(0, Math.round(calculation.totalFinancedAmount)),
        calculation.taxesAndFees,
        taxesAndFees.stateAbbreviation,
      ),
      calculationDetails: {
        apr: resolved.apr,
        creditRating: resolved.preferences.creditRating,
        downPayment: resolved.preferences.downPayment,
        taxesAndFees: calculation.taxesAndFees,
        taxState: taxesAndFees.stateAbbreviation,
        termLength: resolved.preferences.termLength,
      },
    };
  } catch (error) {
    console.info(
      '@kmx/personal-finance-component calculateVehiclePrice error',
      error,
    );
    return null;
  }
}

export function useUpdatePreferences() {
  const { environment, visitorId } = useCalculatorConfig();
  return useCallback(
    async (preferences: IInlinePreferences): Promise<boolean> => {
      const visitorPreferences = { visitorId, ...preferences };
      if (visitorId) {
        await putVisitorCalculatorPreferences(environment, visitorPreferences);
        return true;
      }
      console.error(
        '@kmx/personal-finance-component update preferences called without a visitorId, it is not possible to save preferences without it.',
      );
      return false;
    },
    [environment, visitorId],
  );
}

export function useCalculatorDefaults(_options: Record<string, never>) {
  const { environment, visitorId } = useCalculatorConfig();
  const query = useQuery({
    ...calculatorDefaultsQueryOptions(environment, visitorId),
    enabled: Boolean(visitorId),
    select: withAprByCreditRating,
    placeholderData: fallbackDefaults,
  });
  return { isLoading: query.isLoading, ...(query.data ?? fallbackDefaults) };
}

export function useVisitorPreferences() {
  const { environment, visitorId } = useCalculatorConfig();
  return useQuery({
    ...calculatorDefaultsQueryOptions(environment, visitorId),
    enabled: Boolean(visitorId),
    select: defaults => defaults.preferences,
    placeholderData: fallbackDefaults,
  });
}

interface IUseCalculateMonthlyPaymentParams {
  vehiclePrice: number;
  userStoreId: string;
  overridePreferences?: IInlineRequestPreferences;
  tradeInEquity?: number;
  maxCareCost?: number;
}

export function useCalculateMonthlyPayment({
  vehiclePrice,
  userStoreId,
  overridePreferences,
  tradeInEquity = 0,
  maxCareCost = 0,
}: IUseCalculateMonthlyPaymentParams) {
  const { environment, visitorId } = useCalculatorConfig();
  const preferencesQuery = useQuery(
    visitorPreferencesQueryOptions(environment, visitorId),
  );
  const feeQuery = useQuery(feeScheduleQueryOptions(environment, userStoreId));
  const ratesQuery = useQuery(ratesQueryOptions(environment));
  const isLoading =
    preferencesQuery.isLoading || feeQuery.isLoading || ratesQuery.isLoading;
  const data = useMemo(() => {
    if (
      !preferencesQuery.data ||
      !feeQuery.data ||
      !ratesQuery.data ||
      vehiclePrice <= 0
    ) {
      return null;
    }
    let cleanedOverrides: IInlinePreferences | undefined;
    if (overridePreferences) {
      if (
        overridePreferences.creditRating == null ||
        overridePreferences.downPayment == null ||
        overridePreferences.downPayment < 0 ||
        overridePreferences.termLength == null ||
        Number.isNaN(overridePreferences.termLength) ||
        overridePreferences.termLength <= 0
      ) {
        return null;
      }
      cleanedOverrides = {
        creditRating: overridePreferences.creditRating,
        downPayment: overridePreferences.downPayment,
        monthlyPayment: overridePreferences.monthlyPayment ?? 0,
        prefersLongestTermLength:
          overridePreferences.prefersLongestTermLength ?? true,
        termLength: overridePreferences.termLength,
        hasInteraction: true,
      };
    }
    return buildMonthlyPaymentResponse(
      preferencesQuery.data,
      feeQuery.data,
      ratesQuery.data,
      vehiclePrice,
      cleanedOverrides,
      tradeInEquity,
      maxCareCost,
    );
  }, [
    preferencesQuery.data,
    feeQuery.data,
    ratesQuery.data,
    vehiclePrice,
    overridePreferences,
    tradeInEquity,
    maxCareCost,
  ]);
  return { isLoading, data };
}

interface IUseCalculateVehiclePriceParams {
  userStoreId: string;
  overridePreferences?: IInlineRequestPreferences;
  tradeInEquity?: number;
  maxCareCost?: number;
}

export function useCalculateVehiclePrice({
  userStoreId,
  overridePreferences,
  tradeInEquity = 0,
  maxCareCost = 0,
}: IUseCalculateVehiclePriceParams) {
  const { environment, visitorId } = useCalculatorConfig();
  const preferencesQuery = useQuery(
    visitorPreferencesQueryOptions(environment, visitorId),
  );
  const feeQuery = useQuery(feeScheduleQueryOptions(environment, userStoreId));
  const ratesQuery = useQuery(ratesQueryOptions(environment));
  const isLoading =
    preferencesQuery.isLoading && feeQuery.isLoading && ratesQuery.isLoading;
  const data = useMemo(() => {
    if (!preferencesQuery.data || !feeQuery.data || !ratesQuery.data) {
      return null;
    }
    let cleanedOverrides: IInlinePreferences | undefined;
    if (overridePreferences) {
      if (
        overridePreferences.creditRating == null ||
        overridePreferences.downPayment == null ||
        overridePreferences.downPayment < 0 ||
        overridePreferences.monthlyPayment == null ||
        overridePreferences.monthlyPayment < 0 ||
        overridePreferences.termLength == null ||
        Number.isNaN(overridePreferences.termLength) ||
        overridePreferences.termLength <= 0
      ) {
        return null;
      }
      cleanedOverrides = {
        creditRating: overridePreferences.creditRating,
        downPayment: overridePreferences.downPayment,
        monthlyPayment: overridePreferences.monthlyPayment,
        prefersLongestTermLength:
          overridePreferences.prefersLongestTermLength ?? true,
        termLength: overridePreferences.termLength,
        hasInteraction: true,
      };
    }
    return buildVehiclePriceResponse(
      preferencesQuery.data,
      feeQuery.data,
      ratesQuery.data,
      cleanedOverrides,
      tradeInEquity,
      maxCareCost,
    );
  }, [
    preferencesQuery.data,
    feeQuery.data,
    ratesQuery.data,
    overridePreferences,
    tradeInEquity,
    maxCareCost,
  ]);
  return { isLoading, data };
}
